import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const USER_KEY = "aivos_user";
const TOKEN_KEY = "aivos_token";

export default function GoogleCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const fragment = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
    const params = new URLSearchParams(fragment);
    const token = params.get("token");
    const isNew = params.get("is_new") === "1";
    const onboarded = params.get("onboarded") === "1";

    if (!token) {
      setError("Google sign-in failed. Please try again.");
      return;
    }

    // Strip the fragment immediately — single-use token.
    window.history.replaceState({}, document.title, window.location.pathname);

    (async () => {
      try {
        // Store the bearer token; api.js attaches it as Authorization header.
        localStorage.setItem(TOKEN_KEY, token);
        // Pull the current user with the new token.
        await refresh();
        toast.success(isNew ? "Welcome to AI Visibility OS." : "Welcome back.");
        if (isNew || !onboarded) {
          navigate("/onboarding", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        console.error("google callback failed:", err);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setError(err?.response?.data?.detail || "Google sign-in failed. Please try again.");
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="google-callback-page">
      <div className="max-w-md w-full text-center px-6">
        {error ? (
          <>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">/ auth error</div>
            <h1 className="mt-4 font-marketing text-3xl tracking-tight">{error}</h1>
            <button
              onClick={() => navigate("/login")}
              data-testid="google-callback-back-to-login"
              className="mt-8 px-6 py-3 bg-obsidian text-bone font-app font-bold uppercase tracking-wider hover:bg-intl transition-colors"
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">/ signing you in</div>
            <h1 className="mt-4 font-marketing text-3xl tracking-tight">Authenticating with Google…</h1>
            <div className="mt-8 inline-block w-8 h-8 border-2 border-obsidian border-t-transparent rounded-full animate-spin" />
          </>
        )}
        {/* API base shown only in dev for sanity */}
        <div className="hidden">{API}</div>
      </div>
    </div>
  );
}
